/* eslint-disable import/no-unused-modules */
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { formatPrice as formatPriceRocco } from '@rocco/utils/format-price';
import type { Currency, Proposal, SurchargeConfig } from '../type/types';
import {
    PRODUCT_TABLE_COLUMNS,
    SURCHARGE_CONFIG_AU,
    SURCHARGE_CONFIG_DEFAULT,
    SURCHARGE_CONFIG_NZ,
    SURCHARGE_CONFIG_US,
} from '../constants';

dayjs.extend(timezone);
dayjs.extend(utc);

/** Normalises a finite unix timestamp in seconds or milliseconds to milliseconds. */
const normaliseTimestampMs = (
    timestamp: number | null | undefined,
): number | null => {
    if (!timestamp || !Number.isFinite(timestamp)) {
        return null;
    }

    return timestamp < 10_000_000_000 ? timestamp * 1000 : timestamp;
};

/**
 * Formats a timestamp into a localised date string with day name using local timezone.
 * non-US: ddd D MMM YYYY (e.g. Wed 17 Feb 2026)
 * US: ddd, MMM D, YYYY (e.g. Wed, Feb 17, 2026)
 * @param timestamp - Unix timestamp (seconds or milliseconds)
 * @param country - Country code to determine date format
 * @returns Formatted date string, or empty string if timestamp is null/undefined
 */
export const formatDateWithDayName = (
    timestamp: number | null | undefined,
    country: 'NZ' | 'AU' | 'US',
): string => {
    const timestampMs = normaliseTimestampMs(timestamp);
    if (timestampMs == null) {
        return '';
    }

    if (country === 'US') {
        return dayjs(timestampMs).format('ddd, MMM D, YYYY');
    }

    return dayjs(timestampMs).format('ddd D MMM, YYYY');
};

/** Formats a persisted timestamp with a day name without applying the viewer's timezone. */
const formatUtcDateWithDayName = (
    timestamp: number | null | undefined,
    country: 'NZ' | 'AU' | 'US',
): string => {
    const timestampMs = normaliseTimestampMs(timestamp);
    if (timestampMs == null) {
        return '';
    }

    return country === 'US'
        ? dayjs.utc(timestampMs).format('ddd, MMM D, YYYY')
        : dayjs.utc(timestampMs).format('ddd D MMM, YYYY');
};

/**
 * Returns a localised date string for the invoice.
 * If the proposal has a start and end date, returns a date range (e.g. "Wed 1 Jan 2026 - Fri 31 Jan 2026").
 * Otherwise, returns the proposal's created date.
 * @param proposal - The proposal to extract the invoice date from
 * @returns Formatted date or date range string
 */
export const getInvoiceDateString = (
    proposal: Pick<
        Proposal,
        | 'currency'
        | 'startDate'
        | 'endDate'
        | 'createdAt'
        | 'invoiceSentDate'
        | 'docType'
    >,
    isMobile: boolean,
): string => {
    const country = getCountryByCurrency(proposal.currency);
    if (proposal.startDate && proposal.endDate && !isMobile) {
        const startLabel = formatInvoiceCalendarDateMedium(
            proposal.startDate,
            country,
        );
        const endLabel = formatInvoiceCalendarDateMedium(
            proposal.endDate,
            country,
        );
        if (startLabel && endLabel) {
            return `${startLabel} - ${endLabel}`;
        }
    }

    if (proposal.docType === 'quote') {
        const issueDate =
            normaliseTimestampMs(proposal.invoiceSentDate) ??
            normaliseTimestampMs(proposal.createdAt);

        return formatUtcDateWithDayName(issueDate, country);
    }

    return formatDateWithDayName(proposal.createdAt, country);
};

/**
 * Returns the persisted quote issue date without applying the viewer's timezone.
 * Sent quotes use InvoiceSentDate; legacy and draft quotes fall back to Created.
 */
export const getQuoteDateString = (
    proposal: Pick<Proposal, 'invoiceSentDate' | 'createdAt'>,
    country: 'NZ' | 'AU' | 'US',
): string => {
    const timestampMs =
        normaliseTimestampMs(proposal.invoiceSentDate) ??
        normaliseTimestampMs(proposal.createdAt);
    if (timestampMs == null) {
        return '';
    }

    const date = dayjs.utc(timestampMs);
    if (!date.isValid()) {
        return '';
    }

    return country === 'US'
        ? date.format('MM/DD/YYYY')
        : date.format('DD/MM/YYYY');
};

/**
 * Membership term label value from the invoice GraphQL field (server-rendered).
 */
export const getMembershipTermString = (
    proposal: Pick<Proposal, 'membershipTerm'>,
): string => {
    return proposal.membershipTerm ?? '';
};

/** Whether proposal/BM UIs should render the membership term line. */
export const shouldShowMembershipTerm = (
    proposal: Pick<Proposal, 'hideMembershipTerm' | 'membershipTerm'>,
): boolean => {
    if (proposal.hideMembershipTerm) {
        return false;
    }

    return !!getMembershipTermString(proposal).trim();
};

const ACCEPT_QUOTE_BUTTON_LABEL = 'Accept Quote';
const PARTNERSHIP_ACCEPT_BUTTON_LABEL = 'Confirm my Partnership';

/** Accept CTA label — partnership wording for New Business / Renewals deals. */
export const getQuoteAcceptButtonLabel = (
    isNewBusinessOrRenewalPipeline: boolean | undefined,
): string => {
    return isNewBusinessOrRenewalPipeline
        ? PARTNERSHIP_ACCEPT_BUTTON_LABEL
        : ACCEPT_QUOTE_BUTTON_LABEL;
};

/**
 * Format amount in the given currency.
 * Uses 2-letter format: CC $AMOUNT.00 (e.g. "NZ$ 100.00", "AU$ 1,234.56").
 * Use this for consistent currency display across proposal and invoice modules.
 */
export const formatPriceWithCurrency = (
    amount: number,
    currency: string,
): string => {
    return formatPriceRocco(amount, currency, 'CCC $0.00');
};

/**
 * Format amount in the given currency.
 * Uses 2-letter format: CC $AMOUNT.00 (e.g. "NZ$ 100.00", "AU$ 1,234.56").
 * Use this for consistent currency display across proposal and invoice modules.
 */
export const formatPrice = (amount: number, currency: string): string => {
    return formatPriceRocco(amount, currency, '$0.00');
};

export const getSurchargeConfig = (currency: Currency): SurchargeConfig => {
    switch (currency) {
        case 'NZD':
            return SURCHARGE_CONFIG_NZ;
        case 'AUD':
            return SURCHARGE_CONFIG_AU;
        case 'USD':
            return SURCHARGE_CONFIG_US;
        default:
            return SURCHARGE_CONFIG_DEFAULT;
    }
};

export const applySurcharge = (
    amount: number,
    surchargeConfig: SurchargeConfig,
) => {
    return amount * (1 + surchargeConfig.rate) + surchargeConfig.flatFee;
};

export const removeSurcharge = (
    amountWithSurcharge: number,
    surchargeConfig: SurchargeConfig,
) => {
    return (
        (amountWithSurcharge - surchargeConfig.flatFee) /
        (1 + surchargeConfig.rate)
    );
};

/**
 * Extracts {@code YYYY-MM-DD} from a GraphQL Date / ISO string (embedded calendar date, no TZ shift).
 */
export const extractCalendarDateYmd = (
    iso: string | null | undefined,
): string | null => {
    if (!iso?.trim()) {
        return null;
    }
    const match = iso.trim().match(/^(\d{4}-\d{2}-\d{2})/);
    return match?.[1] ?? null;
};

/**
 * Extracts a calendar {@code YYYY-MM-DD} from a GraphQL Date value without timezone shifting.
 */
export const toCalendarDateYmd = (
    value: Date | string | null | undefined,
): string | undefined => {
    if (value == null) {
        return undefined;
    }
    if (typeof value === 'string') {
        return extractCalendarDateYmd(value) ?? undefined;
    }
    const y = value.getUTCFullYear();
    const m = value.getUTCMonth() + 1;
    const d = value.getUTCDate();
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

/**
 * Canonical invoice calendar date — MM/DD/YYYY (US) or DD/MM/YYYY (NZ/AU).
 * Uses the stored calendar day only; never shifts for the viewer's timezone.
 * Pairs with {@link InvoiceDateTimeHelper::formatMembershipUtcCalendarDate} on the server.
 */
export const formatInvoiceCalendarDate = (
    iso: string | null | undefined,
    country: 'NZ' | 'AU' | 'US',
): string => {
    const datePart = extractCalendarDateYmd(iso);
    if (!datePart) {
        return '';
    }
    const d = dayjs.utc(datePart);
    if (!d.isValid()) {
        return '';
    }
    return country === 'US' ? d.format('MM/DD/YYYY') : d.format('DD/MM/YYYY');
};

/** @deprecated Prefer {@link formatInvoiceCalendarDate} — same behaviour. */
export const formatCalendarDateForDisplay = formatInvoiceCalendarDate;

/**
 * Medium calendar date for invoice UI — no viewer timezone shift.
 * Non-US: D MMM YYYY; US: MMM D, YYYY.
 */
export const formatInvoiceCalendarDateMedium = (
    iso: string | null | undefined,
    country: 'NZ' | 'AU' | 'US',
): string => {
    const datePart = extractCalendarDateYmd(iso);
    if (!datePart) {
        return '';
    }
    const d = dayjs.utc(datePart);
    if (!d.isValid()) {
        return '';
    }
    return country === 'US' ? d.format('MMM D, YYYY') : d.format('D MMM YYYY');
};

/**
 * Parses a GraphQL {@code Date} string (ISO 8601, UTC on the server) for display in the browser's local timezone.
 * Plain calendar dates ({@code YYYY-MM-DD}) are treated as UTC midnight so the calendar day matches stored data.
 */
export const parseApiDateToLocal = (iso: string): dayjs.Dayjs => {
    const trimmed = iso.trim();
    if (!trimmed) {
        return dayjs('');
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return dayjs.utc(`${trimmed}T00:00:00.000Z`).local();
    }
    return dayjs.utc(trimmed).local();
};

/**
 * Numeric date for locale (browser local timezone). Non-US: DD/MM/YYYY; US: MM/DD/YYYY.
 */
export const formatIsoDateForDisplay = (
    iso: string | null | undefined,
    country: 'NZ' | 'AU' | 'US',
): string => {
    if (!iso?.trim()) {
        return '';
    }
    const d = parseApiDateToLocal(iso);
    if (!d.isValid()) {
        return '';
    }
    return country === 'US' ? d.format('MM/DD/YYYY') : d.format('DD/MM/YYYY');
};

/**
 * Numeric date for persisted UTC values without applying the viewer's timezone.
 * Non-US: DD/MM/YYYY; US: MM/DD/YYYY.
 */
export const formatUtcIsoDateForDisplay = (
    iso: string | null | undefined,
    country: 'NZ' | 'AU' | 'US',
): string => {
    if (!iso?.trim()) {
        return '';
    }

    const date = dayjs.utc(iso.trim());
    if (!date.isValid()) {
        return '';
    }

    return country === 'US'
        ? date.format('MM/DD/YYYY')
        : date.format('DD/MM/YYYY');
};

/**
 * Medium date for dialogs (e.g. merge payments). Non-US: D MMM YYYY; US: MMM D, YYYY.
 */
export const formatIsoDateMediumForDisplay = (
    iso: string | null | undefined,
    country: 'NZ' | 'AU' | 'US',
): string => {
    if (!iso?.trim()) {
        return '';
    }
    const d = parseApiDateToLocal(iso);
    if (!d.isValid()) {
        return '';
    }
    return country === 'US' ? d.format('MMM D, YYYY') : d.format('D MMM YYYY');
};

/**
 * Short month style from a UTC instant (ms). Used for quote-deadline copy. non-US: D MMM YYYY; US: MMM D, YYYY.
 */
export const formatTimestampShortMonthByCountry = (
    timestamp: number | null | undefined,
    country: 'NZ' | 'AU' | 'US',
): string => {
    if (!timestamp) {
        return '';
    }
    const ms = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    if (country === 'US') {
        return dayjs(ms).format('MMM D, YYYY');
    }
    return dayjs(ms).format('D MMM YYYY');
};

/**
 * Formats a stored UTC instant as a calendar date (no viewer timezone shift).
 * non-US: DD/MM/YYYY; US: MM/DD/YYYY.
 */
export const formatDate = (
    timestamp: number | null,
    country: 'NZ' | 'AU' | 'US',
): string => {
    if (!timestamp) {
        return '';
    }

    const ms = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    const d = dayjs.utc(ms);
    if (country === 'US') {
        return d.format('MM/DD/YYYY');
    }

    return d.format('DD/MM/YYYY');
};

/**
 * Compact numeric dates for payment receipts (HTML/PDF/email — no client TZ): NZ/AU DD/MM/YY, US MM/DD/YY.
 * Uses the stored calendar day ({@code YYYY-MM-DD} prefix), aligned with {@link formatInvoiceCalendarDate}.
 */
export const formatReceiptNumericDate = (
    dateInput: string | null | undefined,
    country: 'NZ' | 'AU' | 'US',
): string => {
    const datePart = extractCalendarDateYmd(dateInput);
    if (!datePart) {
        return '';
    }
    const d = dayjs.utc(datePart);
    if (!d.isValid()) {
        return '';
    }
    if (country === 'US') {
        return d.format('MM/DD/YY');
    }
    return d.format('DD/MM/YY');
};

/**
 * Tax invoice "Date Issued": 1st of the UTC calendar month/year of the payment due date.
 * Each instalment PDF shows the first of that payment's due month (not quote acceptance).
 */
export const formatReceiptDateIssuedFromDueDate = (
    dueDate: string | null | undefined,
    country: 'NZ' | 'AU' | 'US',
): string => {
    const datePart = extractCalendarDateYmd(dueDate);
    if (!datePart) {
        return '';
    }

    return formatReceiptNumericDate(
        dayjs.utc(datePart).startOf('month').format('YYYY-MM-DD'),
        country,
    );
};

/**
 * Long-form English date for receipt payment rows.
 * US: "March 30, 2026"; NZ/AU: "30 March, 2026" (day-first).
 * Same calendar interpretation as {@link formatReceiptNumericDate}.
 */
export const formatReceiptWrittenDate = (
    dateInput: string | null | undefined,
    country: 'NZ' | 'AU' | 'US',
): string => {
    const datePart = extractCalendarDateYmd(dateInput);
    if (!datePart) {
        return '';
    }
    const d = dayjs.utc(datePart);
    if (!d.isValid()) {
        return '';
    }
    if (country === 'US') {
        return d.format('MMMM D, YYYY');
    }
    return d.format('D MMMM, YYYY');
};

export const getCountryByCurrency = (
    currency: Currency,
): 'NZ' | 'AU' | 'US' => {
    switch (currency) {
        case 'NZD':
            return 'NZ';
        case 'AUD':
            return 'AU';
        case 'USD':
            return 'US';
    }
    return 'NZ';
};

type ProductColumnKey = (typeof PRODUCT_TABLE_COLUMNS)[number]['key'];

/** Returns the Tailwind className for a product table column by key. */
export const getProductColumnClass = (key: ProductColumnKey): string => {
    const col = PRODUCT_TABLE_COLUMNS.find((c) => c.key === key);
    return col?.className ?? '';
};
